import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import SimonProductModal from '../components/SimonProductModal';

const ALL_SOURCE_CODES = [
    'DoraemonSupB',
    'DoraemonSupCD',
    'BeanSup1',
    'BeanSup2',
    'BeanSup3'
];

const createEmptyCostTableRow = () => ({
    effectiveFrom: '',
    DoraemonSupB: '',
    DoraemonSupCD: '',
    BeanSup1: '',
    BeanSup2: '',
    BeanSup3: ''
});

const buildCostTableRows = (supplierSources = []) => {
    const rowMap = new Map();

    supplierSources.forEach((source) => {
        const sourceCode = source?.sourceCode;
        if (!sourceCode || !ALL_SOURCE_CODES.includes(sourceCode)) return;

        (source.costHistory || []).forEach((row) => {
            const effectiveFrom = row?.effectiveFrom
                ? new Date(row.effectiveFrom).toISOString().split('T')[0]
                : '';
            if (!effectiveFrom) return;

            if (!rowMap.has(effectiveFrom)) {
                rowMap.set(effectiveFrom, createEmptyCostTableRow());
                rowMap.get(effectiveFrom).effectiveFrom = effectiveFrom;
            }

            rowMap.get(effectiveFrom)[sourceCode] = row.cost != null ? String(row.cost) : '';
        });
    });

    return [...rowMap.values()].sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom));
};

const getLatestCostForSource = (supplierSources = [], sourceCode) => {
    const source = supplierSources.find((item) => item.sourceCode === sourceCode);
    if (!source || !Array.isArray(source.costHistory) || source.costHistory.length === 0) return null;

    const latestRow = [...source.costHistory]
        .filter((row) => row.effectiveFrom)
        .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))[0];

    return latestRow?.cost ?? null;
};

const getLatestEffectiveDate = (supplierSources = []) => {
    const allDates = supplierSources.flatMap((source) =>
        (source.costHistory || [])
            .map((row) => row.effectiveFrom)
            .filter(Boolean)
    );

    if (allDates.length === 0) return null;
    return new Date(Math.max(...allDates.map((date) => new Date(date).getTime())));
};

const getDisplayUnitPrice = (product) => {
    if (!product) return undefined;
    const h = product.unitPriceHistory;
    if (Array.isArray(h) && h.length > 0) {
        const latest = [...h]
            .filter((row) => row?.effectiveFrom && row?.unitPrice != null)
            .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))[0];
        if (latest) return latest.unitPrice;
    }
    return product.unitPrice;
};

const getLatestUnitPriceEffectiveDate = (product) => {
    const h = product?.unitPriceHistory;
    if (!Array.isArray(h) || !h.length) return null;
    const dates = h.map((row) => row.effectiveFrom).filter(Boolean);
    if (!dates.length) return null;
    return new Date(Math.max(...dates.map((d) => new Date(d).getTime())));
};

const getLatestAnyEffectiveDate = (product) => {
    const costDate = getLatestEffectiveDate(product?.supplierSources || []);
    const upDate = getLatestUnitPriceEffectiveDate(product);
    const times = [costDate, upDate].filter(Boolean).map((d) => new Date(d).getTime());
    if (!times.length) return null;
    return new Date(Math.max(...times));
};

const formatMoneyOrNA = (value) => {
    if (value != null && value !== '') {
        return `$${Number(value).toFixed(2)}`;
    }

    return <span className="text-red-600 font-medium">N/A</span>;
};

const SimonProductPage = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [supplierFilter, setSupplierFilter] = useState('all');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchMeta = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`,
                'ngrok-skip-browser-warning': 'true'
            };
            const [categoryResponse, supplierResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonproducts/categories`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonproducts/suppliers`, { headers })
            ]);
            if (categoryResponse.data.success) setCategories(categoryResponse.data.data || []);
            if (supplierResponse.data.success) setSuppliers(supplierResponse.data.data || []);
        } catch (err) {
            // Non-blocking
        }
    };

    const fetchProducts = async (page = 1, search = '', category = 'all', supplier = 'all') => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/simonproducts?page=${page}&limit=20`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
            if (category !== 'all') apiUrl += `&category=${encodeURIComponent(category)}`;
            if (supplier !== 'all') apiUrl += `&supplier=${encodeURIComponent(supplier)}`;

            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            });

            if (response.data.success) {
                setProducts(response.data.data);
                setTotalProducts(response.data.pagination.total);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch Simon products: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeta();
    }, []);

    useEffect(() => {
        fetchProducts(currentPage, searchTerm, categoryFilter, supplierFilter);
    }, [currentPage, searchTerm, categoryFilter, supplierFilter]);

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCategoryFilter('all');
        setSupplierFilter('all');
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (!selectedProduct) return;
        try {
            setDeleteLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/simonproducts/${selectedProduct._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowDeleteModal(false);
            setSelectedProduct(null);
            fetchProducts(currentPage, searchTerm, categoryFilter, supplierFilter);
        } catch (err) {
            setError(`Failed to delete Simon product: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Simon Products</h1>
                        <p className="text-gray-600">Total: {totalProducts} products</p>
                    </div>
                    <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center">
                        <FiPlus className="mr-2" />
                        Create New Product
                    </button>
                </div>

                <div className="mb-6">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                placeholder="Search by category, product name, remarks, or source code..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 rounded-md">
                            <option value="all">All Categories</option>
                            {categories.map((category) => <option key={category} value={category}>{category}</option>)}
                        </select>
                        <select value={supplierFilter} onChange={(e) => { setSupplierFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 rounded-md">
                            <option value="all">All Suppliers</option>
                            {suppliers.map((supplier) => <option key={supplier} value={supplier}>{supplier}</option>)}
                        </select>
                        <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"><FiSearch className="mr-2" />Search</button>
                        <button onClick={handleClear} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">Clear</button>
                    </div>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                {showCreateModal && (
                    <SimonProductModal
                        mode="create"
                        categories={categories}
                        suppliers={suppliers}
                        onSuccess={() => {
                            setShowCreateModal(false);
                            fetchProducts(currentPage, searchTerm, categoryFilter, supplierFilter);
                        }}
                        onCancel={() => setShowCreateModal(false)}
                    />
                )}

                {showEditModal && selectedProduct && (
                    <SimonProductModal
                        mode="edit"
                        product={selectedProduct}
                        categories={categories}
                        suppliers={suppliers}
                        onSuccess={() => {
                            setShowEditModal(false);
                            setSelectedProduct(null);
                            fetchProducts(currentPage, searchTerm, categoryFilter, supplierFilter);
                        }}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedProduct(null);
                        }}
                    />
                )}

                {showDeleteModal && selectedProduct && (
                    <DeleteConfirmationModal
                        title="Delete Simon Product"
                        message="Are you sure you want to delete this product?"
                        itemName={selectedProduct.productName}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => {
                            setShowDeleteModal(false);
                            setSelectedProduct(null);
                        }}
                    />
                )}

                {showViewModal && selectedProduct && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border max-w-5xl w-full shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center border-b pb-4 mb-4">
                                <h3 className="text-xl font-medium">
                                    Simon Product Details - <span className="text-blue-700 font-bold">{selectedProduct.productName}</span>
                                </h3>
                                <button onClick={() => { setShowViewModal(false); setSelectedProduct(null); }} className="text-gray-500 hover:text-gray-700">
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-6">
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div><span className="font-semibold text-gray-700">Category:</span> <span className="text-blue-700">{selectedProduct.category}</span></div>
                                        <div><span className="font-semibold text-gray-700">Unit Price (latest):</span> <span className="text-blue-700">{formatMoneyOrNA(getDisplayUnitPrice(selectedProduct))}</span></div>
                                        <div><span className="font-semibold text-gray-700">As Of (cost or unit price):</span> <span className="text-blue-700">{getLatestAnyEffectiveDate(selectedProduct) ? getLatestAnyEffectiveDate(selectedProduct).toLocaleDateString('en-SG') : 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Order Count:</span> <span className="text-blue-700">{selectedProduct.orderCount || 0}</span></div>
                                        <div><span className="font-semibold text-gray-700">Total Revenue:</span> <span className="text-blue-700">${Number(selectedProduct.totalRevenue || 0).toFixed(2)}</span></div>
                                        <div><span className="font-semibold text-gray-700">Active:</span> <span className="text-blue-700">{selectedProduct.isActive ? 'Yes' : 'No'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Remarks:</span> <span className="text-blue-700">{selectedProduct.remarks || 'N/A'}</span></div>
                                    </div>
                                </section>
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Unit Price History</h4>
                                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Unit Price</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {!selectedProduct.unitPriceHistory?.length ? (
                                                    <tr>
                                                        <td colSpan="2" className="px-3 py-4 text-center text-gray-500">No unit price history</td>
                                                    </tr>
                                                ) : (
                                                    [...selectedProduct.unitPriceHistory]
                                                        .filter((row) => row?.effectiveFrom)
                                                        .sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom))
                                                        .map((row, index) => (
                                                            <tr key={`view-up-row-${index}`}>
                                                                <td className="px-3 py-2">{new Date(row.effectiveFrom).toLocaleDateString('en-SG')}</td>
                                                                <td className="px-3 py-2">{row.unitPrice != null ? `$${Number(row.unitPrice).toFixed(2)}` : '—'}</td>
                                                            </tr>
                                                        ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Supplier Source & Cost History</h4>
                                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">DoraemonSupB</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">DoraemonSupCD</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">BeanSup1</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">BeanSup2</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">BeanSup3</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {buildCostTableRows(selectedProduct.supplierSources).length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="px-3 py-4 text-center text-gray-500">No cost history</td>
                                                    </tr>
                                                ) : buildCostTableRows(selectedProduct.supplierSources).map((row, index) => (
                                                    <tr key={`view-cost-row-${index}`}>
                                                        <td className="px-3 py-2">{row.effectiveFrom ? new Date(row.effectiveFrom).toLocaleDateString('en-SG') : 'N/A'}</td>
                                                        {ALL_SOURCE_CODES.map((sourceCode) => (
                                                            <td key={`${sourceCode}-${index}`} className="px-3 py-2">
                                                                {row[sourceCode] !== '' ? `$${Number(row[sourceCode]).toFixed(2)}` : '—'}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DoraemonSupB</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DoraemonSupCD</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BeanSup1</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BeanSup2</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BeanSup3</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">As Of</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Count</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan="12" className="px-6 py-4 text-center">Loading...</td></tr>
                            ) : products.length === 0 ? (
                                <tr><td colSpan="12" className="px-6 py-4 text-center">No Simon products found</td></tr>
                            ) : (
                                products.map((product) => (
                                    <tr key={product._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{product.category}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{product.productName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatMoneyOrNA(getDisplayUnitPrice(product))}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{getLatestCostForSource(product.supplierSources, 'DoraemonSupB') != null ? `$${Number(getLatestCostForSource(product.supplierSources, 'DoraemonSupB')).toFixed(2)}` : '—'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{getLatestCostForSource(product.supplierSources, 'DoraemonSupCD') != null ? `$${Number(getLatestCostForSource(product.supplierSources, 'DoraemonSupCD')).toFixed(2)}` : '—'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{getLatestCostForSource(product.supplierSources, 'BeanSup1') != null ? `$${Number(getLatestCostForSource(product.supplierSources, 'BeanSup1')).toFixed(2)}` : '—'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{getLatestCostForSource(product.supplierSources, 'BeanSup2') != null ? `$${Number(getLatestCostForSource(product.supplierSources, 'BeanSup2')).toFixed(2)}` : '—'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{getLatestCostForSource(product.supplierSources, 'BeanSup3') != null ? `$${Number(getLatestCostForSource(product.supplierSources, 'BeanSup3')).toFixed(2)}` : '—'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{getLatestAnyEffectiveDate(product) ? getLatestAnyEffectiveDate(product).toLocaleDateString('en-SG') : 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{product.orderCount || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">${Number(product.totalRevenue || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button onClick={() => { setSelectedProduct(product); setShowViewModal(true); }} className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center" title="View Details"><FiEye className="w-4 h-4" /></button>
                                            <button onClick={() => { setSelectedProduct(product); setShowEditModal(true); }} className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center" title="Edit Product"><FiEdit2 className="w-4 h-4" /></button>
                                            <button onClick={() => { setSelectedProduct(product); setShowDeleteModal(true); }} className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center" title="Delete Product"><FiTrash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
        </Layout>
    );
};

export default SimonProductPage;
